import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ArrowLeft, Activity, AlertTriangle, ShieldOff,
  Gauge, Bell, BellOff, LineChart, Database, Zap, BellRing, Send,
  Settings, Eye, EyeOff, Check,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONES = {
  '2xx': 'bg-emerald-100 text-emerald-700',
  '3xx': 'bg-sky-100 text-sky-700',
  '4xx': 'bg-amber-100 text-amber-700',
  '429': 'bg-orange-100 text-orange-700',
  '5xx': 'bg-red-100 text-red-700',
};

// Treat any of these as "the domain is unhealthy"
const ERR_KEYS = ['4xx', '5xx', '429'];

const Tile = ({ testid, label, value, icon: Icon, tone, hint }) => (
  <div data-testid={testid} className="cn-card p-5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</p>
        <p className="cn-display text-2xl text-cn-text mt-1.5 tabular-nums truncate">{value}</p>
        {hint && <p className="text-[11px] text-cn-text-muted mt-1">{hint}</p>}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </div>
);

const StatusPill = ({ kind, count }) => {
  if (!count) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${STATUS_TONES[kind]}`}
      data-testid={`obs-status-${kind}`}
    >
      {kind.toUpperCase()} <span className="opacity-80">{count}</span>
    </span>
  );
};

// Color band based on error % so the eye finds the unhealthy domains fast.
const HEALTH_BAND = (errPct) => {
  if (errPct >= 25) return 'border-l-4 border-red-500 bg-red-50/40';
  if (errPct >= 10) return 'border-l-4 border-amber-400 bg-amber-50/40';
  if (errPct > 0) return 'border-l-4 border-amber-200';
  return 'border-l-4 border-emerald-200';
};

/**
 * Tiny pure-SVG sparkline. Draws a filled area + line for the given series.
 * - `points`: array of numeric values (left → right)
 * - `width`/`height`: chart dimensions in px
 * - `stroke`/`fill`: tailwind-ish colors as CSS strings (we use inline SVG attrs)
 * No dependency on recharts/chart.js — keeps bundle lean.
 */
const Sparkline = ({ points, width = 560, height = 80, stroke = '#1e40af', fill = 'rgba(30,64,175,0.12)', testid }) => {
  if (!points || points.length < 2) {
    return (
      <div
        className="h-20 flex items-center justify-center text-[11px] text-cn-text-muted bg-slate-50 rounded"
        data-testid={testid ? `${testid}-empty` : undefined}
      >
        Collecting samples — first datapoint arrives after 2 rollup intervals (~2 min).
      </div>
    );
  }
  const max = Math.max(1, ...points);
  const stepX = points.length > 1 ? width / (points.length - 1) : width;
  const coords = points.map((v, i) => {
    const x = i * stepX;
    const y = height - (v / max) * (height - 4) - 2; // 2px breathing room top + bottom
    return [x, y];
  });
  const linePath = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block w-full" data-testid={testid}>
      <path d={areaPath} fill={fill} />
      <path d={linePath} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {/* Last point dot */}
      {coords.length > 0 && (
        <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r="2.5" fill={stroke} />
      )}
    </svg>
  );
};

export default function AdminObservability() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [snap, setSnap] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyHours, setHistoryHours] = useState(1);
  const [slow, setSlow] = useState(null);
  const [alertsInfo, setAlertsInfo] = useState(null);
  const [firingTest, setFiringTest] = useState(false);
  const [sinks, setSinks] = useState(null);
  const [sinkEditorOpen, setSinkEditorOpen] = useState(false);
  const [sinkDraft, setSinkDraft] = useState({ slack_webhook_url: '', pagerduty_routing_key: '' });
  const [savingSinks, setSavingSinks] = useState(false);
  const [rulesEditorOpen, setRulesEditorOpen] = useState(false);
  const [rulesDraft, setRulesDraft] = useState([]);
  const [savingRules, setSavingRules] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [windowSec, setWindowSec] = useState(60);

  const authHeader = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/observability/domains?window=${windowSec}`,
        authHeader,
      );
      setSnap(data);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || 'Could not load observability snapshot');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, windowSec]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/observability/history?hours=${historyHours}`,
        authHeader,
      );
      setHistory(data);
    } catch (e) {
      // History is best-effort; don't surface errors aggressively.
      setHistory({ samples: 0, points: [], rollup_enabled: false, error: e.response?.data?.detail });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, historyHours]);

  const loadSlow = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/observability/slow-endpoints?window=${windowSec}&top=5`,
        authHeader,
      );
      setSlow(data);
    } catch {
      // Slow-endpoints panel is best-effort.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, windowSec]);

  const loadAlerts = useCallback(async () => {
    if (!token) return;
    try {
      const [rules, state, sinksRes] = await Promise.all([
        axios.get(`${API}/admin/alerts/rules`, authHeader),
        axios.get(`${API}/admin/alerts/state`, authHeader),
        axios.get(`${API}/admin/alerts/sinks`, authHeader),
      ]);
      setAlertsInfo({ rules: rules.data, state: state.data });
      setSinks(sinksRes.data);
    } catch {
      // Alerts surface is best-effort — never block the dashboard on its failure.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const saveSinks = async () => {
    setSavingSinks(true);
    try {
      const body = {};
      // Only send fields the admin actually typed into — leave others alone.
      if (sinkDraft.slack_webhook_url !== undefined && sinkDraft.slack_webhook_url !== null) {
        body.slack_webhook_url = sinkDraft.slack_webhook_url;
      }
      if (sinkDraft.pagerduty_routing_key !== undefined && sinkDraft.pagerduty_routing_key !== null) {
        body.pagerduty_routing_key = sinkDraft.pagerduty_routing_key;
      }
      const { data } = await axios.put(`${API}/admin/alerts/sinks`, body, authHeader);
      setSinks(data);
      toast.success('Webhooks saved');
      setSinkEditorOpen(false);
      setSinkDraft({ slack_webhook_url: '', pagerduty_routing_key: '' });
      loadAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save webhooks');
    } finally {
      setSavingSinks(false);
    }
  };

  const openRulesEditor = () => {
    setRulesDraft(JSON.parse(JSON.stringify(alertsInfo?.rules?.rules || [])));
    setRulesEditorOpen(true);
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      await axios.put(`${API}/admin/alerts/rules`, { rules: rulesDraft }, authHeader);
      toast.success('Rules saved');
      setRulesEditorOpen(false);
      loadAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save rules');
    } finally {
      setSavingRules(false);
    }
  };

  const sendTestFire = async () => {
    setFiringTest(true);
    try {
      const { data } = await axios.post(`${API}/admin/alerts/test-fire`, {}, authHeader);
      const sinks = data?.fired?.sinks || {};
      const reachedAny = sinks.slack || sinks.pagerduty;
      toast.success(
        reachedAny
          ? `Test alert sent (slack=${sinks.slack ? 'on' : 'off'}, pd=${sinks.pagerduty ? 'on' : 'off'})`
          : "Test alert logged to Mongo. No sinks configured — set SLACK_WEBHOOK_URL or PAGERDUTY_ROUTING_KEY in backend/.env.",
      );
      // Reload the state so the new fire shows in "recent fires" right away.
      loadAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test fire failed');
    } finally {
      setFiringTest(false);
    }
  };

  useEffect(() => { if (token) load(); }, [load, token]);
  useEffect(() => { if (token) loadHistory(); }, [loadHistory, token]);
  useEffect(() => { if (token) loadSlow(); }, [loadSlow, token]);
  useEffect(() => { if (token) loadAlerts(); }, [loadAlerts, token]);

  // Auto-refresh every 10s while the page is open and toggle is on.
  // `load` is memoized via useCallback so the interval only resets when the window changes.
  useEffect(() => {
    if (!autoRefresh || !token) return undefined;
    const tick = () => { load(true); loadSlow(); };
    const id = setInterval(tick, 10_000);
    return () => clearInterval(id);
  }, [autoRefresh, token, load, loadSlow]);

  // History refreshes less aggressively — every 60s (matches the rollup cadence)
  useEffect(() => {
    if (!autoRefresh || !token) return undefined;
    const tick = () => { loadHistory(); };
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [autoRefresh, token, loadHistory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg flex items-center justify-center" data-testid="obs-loading">
        <Loader2 className="w-6 h-6 animate-spin text-cn-primary" />
      </div>
    );
  }

  const domains = snap?.domains || [];
  const registeredOnly = (snap?.registered_domains || []).filter(
    (d) => !domains.some((row) => row.domain === d),
  );
  const errorTotal = (snap?.total_4xx || 0) + (snap?.total_5xx || 0) + (snap?.total_429 || 0);
  const errorPct = snap?.total_requests
    ? Math.round((errorTotal * 1000) / snap.total_requests) / 10
    : 0;

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="admin-observability-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="inline-flex items-center gap-1 text-xs font-semibold text-cn-text-muted hover:text-cn-primary mb-3"
              data-testid="obs-back-btn"
            >
              <ArrowLeft className="w-3 h-3" /> Admin dashboard
            </button>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-cn-primary text-white flex items-center justify-center">
                <Activity className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="cn-display text-3xl text-cn-text">Live Observability</h1>
                  <span
                    className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                      snap?.rate_limit_enforced
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-300 text-slate-700'
                    }`}
                    data-testid="obs-rate-limit-badge"
                  >
                    {snap?.rate_limit_enforced ? 'Limits enforced' : 'Limits OFF (dev)'}
                  </span>
                  {snap?.sentry_enabled && (
                    <span
                      className="px-2 py-0.5 rounded-md bg-violet-600 text-white text-[10px] font-bold uppercase tracking-wider"
                      data-testid="obs-sentry-badge"
                    >
                      Sentry on
                    </span>
                  )}
                </div>
                <p className="text-sm text-cn-text-muted">
                  Real-time per-domain request counts, error rates and latency.
                  Auto-polls every 10s — no Sentry-ingestion delay.
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={windowSec}
              onChange={(e) => setWindowSec(Number(e.target.value))}
              className="cn-select !py-1.5 !text-xs"
              data-testid="obs-window-select"
            >
              <option value={30}>Last 30 sec</option>
              <option value={60}>Last 60 sec</option>
              <option value={120}>Last 2 min</option>
              <option value={300}>Last 5 min</option>
            </select>
            <button
              onClick={() => setAutoRefresh((v) => !v)}
              className={`cn-btn cn-btn-ghost !py-2 text-xs ${autoRefresh ? '' : 'opacity-60'}`}
              data-testid="obs-autorefresh-toggle"
              title={autoRefresh ? 'Auto-refresh ON (10s)' : 'Auto-refresh OFF'}
            >
              {autoRefresh ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
              {' '}{autoRefresh ? 'Auto' : 'Manual'}
            </button>
            <button
              onClick={() => load()}
              disabled={refreshing}
              className="cn-btn cn-btn-ghost !py-2 text-xs"
              data-testid="obs-refresh-btn"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {' '}Refresh
            </button>
          </div>
        </div>

        {/* Top-of-funnel tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Tile
            testid="obs-tile-total"
            label={`Requests · last ${windowSec}s`}
            value={snap?.total_requests?.toLocaleString() ?? '0'}
            icon={Activity}
            tone="bg-cn-primary/10 text-cn-primary"
          />
          <Tile
            testid="obs-tile-err-pct"
            label="Error rate"
            value={`${errorPct}%`}
            icon={AlertTriangle}
            tone={errorPct >= 5 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}
            hint={`${errorTotal} non-2xx total`}
          />
          <Tile
            testid="obs-tile-rate-limited"
            label="Rate-limited (429)"
            value={(snap?.total_429 || 0).toLocaleString()}
            icon={ShieldOff}
            tone={(snap?.total_429 || 0) > 0 ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-500'}
          />
          <Tile
            testid="obs-tile-server-errors"
            label="Server errors (5xx)"
            value={(snap?.total_5xx || 0).toLocaleString()}
            icon={AlertTriangle}
            tone={(snap?.total_5xx || 0) > 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}
          />
        </div>

        {/* Active domains */}
        <div className="cn-card p-5 mb-6" data-testid="obs-active-domains">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h2 className="cn-display text-lg text-cn-text">Active domains</h2>
            <p className="text-[11px] text-cn-text-muted">Sorted by traffic, last {windowSec}s</p>
          </div>
          {domains.length === 0 ? (
            <p className="text-sm text-cn-text-muted py-4" data-testid="obs-no-traffic">
              No traffic recorded in this window. (Hit a few endpoints and this list will populate.)
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="obs-domains-table">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-cn-text-muted text-left border-b border-slate-200">
                    <th className="py-2 pr-4">Domain</th>
                    <th className="py-2 pr-4 text-right">Requests</th>
                    <th className="py-2 pr-4 text-right">RPS</th>
                    <th className="py-2 pr-4 text-right">p50</th>
                    <th className="py-2 pr-4 text-right">p95</th>
                    <th className="py-2 pr-4 text-right">Err %</th>
                    <th className="py-2 pr-4">Status mix</th>
                    <th className="py-2 pr-4 text-right">Limit</th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map((d) => (
                    <tr
                      key={d.domain}
                      className={`${HEALTH_BAND(d.err_pct)} hover:bg-cn-bg`}
                      data-testid={`obs-row-${d.domain}`}
                    >
                      <td className="py-2 pl-2 pr-4 font-mono text-xs font-semibold text-cn-text">{d.domain}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{d.total.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-cn-text-muted">{d.rps.toFixed(2)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-cn-text-muted">
                        {d.p50_ms != null ? `${d.p50_ms}ms` : '—'}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums text-cn-text-muted">
                        {d.p95_ms != null ? `${d.p95_ms}ms` : '—'}
                      </td>
                      <td
                        className={`py-2 pr-4 text-right tabular-nums font-semibold ${
                          d.err_pct >= 25 ? 'text-red-600' : d.err_pct >= 10 ? 'text-amber-600' : 'text-cn-text-muted'
                        }`}
                      >
                        {d.err_pct}%
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-1 flex-wrap">
                          <StatusPill kind="2xx" count={d.by_status['2xx']} />
                          <StatusPill kind="3xx" count={d.by_status['3xx']} />
                          <StatusPill kind="4xx" count={d.by_status['4xx']} />
                          <StatusPill kind="429" count={d.by_status['429']} />
                          <StatusPill kind="5xx" count={d.by_status['5xx']} />
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums text-cn-text-muted">
                        {d.limit_per_min}/min
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top 5 slowest endpoints — drills "billing domain is slow" → "the
            specific route is the offender". Sourced from the per-route ring
            buffer in `routes/_middleware.py::_ROUTE_BUCKETS`. */}
        <div className="cn-card p-5 mb-6" data-testid="obs-slow-endpoints-panel">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-cn-text-muted" />
                <h2 className="cn-display text-base text-cn-text">
                  Top 5 slowest endpoints
                </h2>
                <span className="text-[11px] text-cn-text-muted">· last {windowSec}s</span>
              </div>
              <p className="text-[11px] text-cn-text-muted mt-1">
                Routes with ≥3 samples in the window, sorted by p95 latency.
                Spot the silently-degrading endpoint before users complain.
              </p>
            </div>
          </div>
          {slow && slow.endpoints && slow.endpoints.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="obs-slow-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-cn-text-muted text-left">
                    <th className="py-1.5 pr-3 font-bold">#</th>
                    <th className="py-1.5 pr-3 font-bold">Route</th>
                    <th className="py-1.5 pr-3 font-bold text-right">Count</th>
                    <th className="py-1.5 pr-3 font-bold text-right">p50</th>
                    <th className="py-1.5 pr-3 font-bold text-right">p95</th>
                    <th className="py-1.5 pr-3 font-bold text-right">Max</th>
                    <th className="py-1.5 pr-3 font-bold text-right">Err %</th>
                  </tr>
                </thead>
                <tbody>
                  {slow.endpoints.map((ep, i) => {
                    // Highlight rows with bad SLO indicators.
                    const slow95 = ep.p95_ms >= 1000;
                    const erroring = ep.err_pct >= 10;
                    const rowClass = erroring
                      ? 'border-l-4 border-red-500 bg-red-50/30'
                      : slow95
                        ? 'border-l-4 border-amber-400 bg-amber-50/30'
                        : 'border-l-4 border-transparent';
                    return (
                      <tr
                        key={ep.route}
                        className={`${rowClass} hover:bg-slate-50/60`}
                        data-testid={`obs-slow-row-${i}`}
                      >
                        <td className="py-1.5 pr-3 text-[11px] text-cn-text-muted tabular-nums">{i + 1}</td>
                        <td className="py-1.5 pr-3 font-mono text-[12px] truncate max-w-md" title={ep.route}>
                          {ep.route}
                        </td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{ep.count}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{ep.p50_ms}ms</td>
                        <td className={`py-1.5 pr-3 text-right tabular-nums ${slow95 ? 'font-bold text-amber-700' : ''}`}>
                          {ep.p95_ms}ms
                        </td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{ep.max_ms}ms</td>
                        <td className={`py-1.5 pr-3 text-right tabular-nums ${erroring ? 'font-bold text-red-600' : ''}`}>
                          {ep.err_pct}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              className="h-16 flex items-center justify-center text-[11px] text-cn-text-muted bg-slate-50 rounded"
              data-testid="obs-slow-empty"
            >
              No route has ≥3 samples in the {windowSec}s window yet — generate traffic and check back.
            </div>
          )}
        </div>

        {/* Alerts status — wakes ops on threshold breach. Configure
            SLACK_WEBHOOK_URL and/or PAGERDUTY_ROUTING_KEY in backend/.env
            to enable actual notifications. Rules + thresholds are edited
            via PUT /api/admin/alerts/rules (no UI editor yet). */}
        <div className="cn-card p-5 mb-6" data-testid="obs-alerts-panel">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <BellRing className="w-4 h-4 text-cn-text-muted" />
                <h2 className="cn-display text-base text-cn-text">Alerting</h2>
                {alertsInfo?.rules?.enabled === false && (
                  <span className="px-2 py-0.5 rounded-md bg-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-wider">
                    Loop disabled
                  </span>
                )}
                <span
                  className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                    alertsInfo?.rules?.sinks?.slack_configured
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                  data-testid="obs-alerts-slack-badge"
                >
                  Slack {alertsInfo?.rules?.sinks?.slack_configured ? 'ON' : 'OFF'}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                    alertsInfo?.rules?.sinks?.pagerduty_configured
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                  data-testid="obs-alerts-pagerduty-badge"
                >
                  PagerDuty {alertsInfo?.rules?.sinks?.pagerduty_configured ? 'ON' : 'OFF'}
                </span>
              </div>
              <p className="text-[11px] text-cn-text-muted mt-1">
                {alertsInfo?.rules
                  ? `${alertsInfo.rules.rules?.length || 0} rule${(alertsInfo.rules.rules?.length || 0) === 1 ? '' : 's'} active · evaluates every ${alertsInfo.rules.evaluation_interval_seconds}s · cooldown ${alertsInfo.rules.cooldown_seconds}s`
                  : 'Loading alert state…'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={openRulesEditor}
                className="cn-btn cn-btn-ghost !py-1.5 !text-xs flex items-center gap-1.5"
                data-testid="obs-alerts-edit-rules"
              >
                <Settings className="w-3.5 h-3.5" /> Edit rules
              </button>
              <button
                onClick={() => setSinkEditorOpen((v) => !v)}
                className="cn-btn cn-btn-ghost !py-1.5 !text-xs flex items-center gap-1.5"
                data-testid="obs-alerts-edit-sinks"
              >
                <Settings className="w-3.5 h-3.5" /> Webhooks
              </button>
              <button
                onClick={sendTestFire}
                disabled={firingTest}
                className="cn-btn cn-btn-secondary !py-1.5 !text-xs flex items-center gap-1.5"
                data-testid="obs-alerts-test-fire"
              >
                {firingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test fire
              </button>
            </div>
          </div>
          {alertsInfo?.state?.recent_fires && alertsInfo.state.recent_fires.length > 0 ? (
            <div className="text-[12px] space-y-1" data-testid="obs-alerts-recent">
              {alertsInfo.state.recent_fires.slice(0, 5).map((f) => (
                <div
                  key={f.id}
                  className={`flex items-center gap-2 px-2 py-1 rounded ${
                    f.severity === 'critical' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'
                  }`}
                >
                  <AlertTriangle className="w-3 h-3" />
                  <span className="font-mono text-[10px]">{f.fired_at?.slice(0, 19).replace('T', ' ')}</span>
                  <span className="font-bold uppercase text-[10px]">{f.severity}</span>
                  <span className="flex-1 truncate" title={f.summary}>{f.rule_name}</span>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="h-12 flex items-center justify-center text-[11px] text-cn-text-muted bg-slate-50 rounded"
              data-testid="obs-alerts-empty"
            >
              No alerts have fired yet — the loop is healthy.
            </div>
          )}

          {/* Inline sink editor — collapsible, opens via "Webhooks" button above. */}
          {sinkEditorOpen && (
            <div className="mt-5 pt-5 border-t border-cn-border/60 space-y-4" data-testid="obs-alerts-sink-editor">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm text-cn-text">Webhook destinations</h3>
                <p className="text-[11px] text-cn-text-muted">Stored encrypted in Mongo. Override env vars without redeploying.</p>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-cn-text-muted uppercase tracking-wider mb-1.5">Slack incoming-webhook URL</label>
                  <div className="text-[11px] text-cn-text-muted mb-1.5">
                    Current: {sinks?.slack?.configured ? (
                      <>
                        <span className="font-mono">{sinks.slack.preview}</span> (<span className="font-semibold">{sinks.slack.source}</span>)
                      </>
                    ) : (
                      <span className="italic">not configured</span>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="https://hooks.slack.com/services/T…/B…/…"
                    value={sinkDraft.slack_webhook_url}
                    onChange={(e) => setSinkDraft({ ...sinkDraft, slack_webhook_url: e.target.value })}
                    className="cn-input w-full !text-xs font-mono"
                    data-testid="obs-alerts-slack-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-cn-text-muted uppercase tracking-wider mb-1.5">PagerDuty Events v2 routing key</label>
                  <div className="text-[11px] text-cn-text-muted mb-1.5">
                    Current: {sinks?.pagerduty?.configured ? (
                      <>
                        <span className="font-mono">{sinks.pagerduty.preview}</span> (<span className="font-semibold">{sinks.pagerduty.source}</span>)
                      </>
                    ) : (
                      <span className="italic">not configured</span>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="32-character hex routing key"
                    value={sinkDraft.pagerduty_routing_key}
                    onChange={(e) => setSinkDraft({ ...sinkDraft, pagerduty_routing_key: e.target.value })}
                    className="cn-input w-full !text-xs font-mono"
                    data-testid="obs-alerts-pagerduty-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={saveSinks}
                  disabled={savingSinks}
                  className="cn-btn cn-btn-primary !py-1.5 !text-xs flex items-center gap-1.5"
                  data-testid="obs-alerts-save-sinks"
                >
                  {savingSinks ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Save webhooks
                </button>
                <button
                  onClick={() => { setSinkEditorOpen(false); setSinkDraft({ slack_webhook_url: '', pagerduty_routing_key: '' }); }}
                  className="cn-btn cn-btn-ghost !py-1.5 !text-xs"
                  data-testid="obs-alerts-cancel-sinks"
                >
                  Cancel
                </button>
                <p className="text-[11px] text-cn-text-muted ml-auto">Tip: send a blank string to clear an override and fall back to .env.</p>
              </div>
            </div>
          )}

          {/* Inline rules editor — opens via "Edit rules" button above. */}
          {rulesEditorOpen && (
            <div className="mt-5 pt-5 border-t border-cn-border/60 space-y-3" data-testid="obs-alerts-rules-editor">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-sm text-cn-text">Alert rules ({rulesDraft.length})</h3>
                <button
                  onClick={() => setRulesDraft([...rulesDraft, {
                    id: `custom-${Date.now()}`, name: 'New rule', kind: 'error_rate_pct',
                    target: null, threshold: 10, min_consecutive: 1, window_seconds: 60,
                    severity: 'warning', enabled: true,
                  }])}
                  className="cn-btn cn-btn-ghost !py-1.5 !text-xs"
                  data-testid="obs-alerts-add-rule"
                >+ Add rule</button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="text-cn-text-muted">
                    <tr className="text-left">
                      <th className="py-1.5 pr-2">On</th>
                      <th className="py-1.5 pr-2">Name</th>
                      <th className="py-1.5 pr-2">Kind</th>
                      <th className="py-1.5 pr-2">Target</th>
                      <th className="py-1.5 pr-2 text-right">≥</th>
                      <th className="py-1.5 pr-2 text-right">×</th>
                      <th className="py-1.5 pr-2 text-right">window</th>
                      <th className="py-1.5 pr-2">severity</th>
                      <th className="py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rulesDraft.map((r, i) => (
                      <tr key={r.id} className="border-t border-cn-border/50" data-testid={`obs-alerts-rule-${i}`}>
                        <td className="py-1.5 pr-2">
                          <input type="checkbox" checked={r.enabled !== false} onChange={(e) => { const next = [...rulesDraft]; next[i] = { ...r, enabled: e.target.checked }; setRulesDraft(next); }} />
                        </td>
                        <td className="py-1.5 pr-2">
                          <input type="text" value={r.name || ''} onChange={(e) => { const next = [...rulesDraft]; next[i] = { ...r, name: e.target.value }; setRulesDraft(next); }} className="cn-input !py-1 !text-[11px] w-full" />
                        </td>
                        <td className="py-1.5 pr-2">
                          <select value={r.kind} onChange={(e) => { const next = [...rulesDraft]; next[i] = { ...r, kind: e.target.value }; setRulesDraft(next); }} className="cn-select !py-1 !text-[11px]">
                            <option value="error_rate_pct">error_rate_pct</option>
                            <option value="rate_limited_count">rate_limited_count</option>
                            <option value="server_errors_count">server_errors_count</option>
                            <option value="domain_p95_ms">domain_p95_ms</option>
                          </select>
                        </td>
                        <td className="py-1.5 pr-2">
                          <input type="text" value={r.target || ''} placeholder={r.kind === 'domain_p95_ms' ? 'ai' : '—'} onChange={(e) => { const next = [...rulesDraft]; next[i] = { ...r, target: e.target.value || null }; setRulesDraft(next); }} className="cn-input !py-1 !text-[11px] w-16" />
                        </td>
                        <td className="py-1.5 pr-2 text-right">
                          <input type="number" value={r.threshold} onChange={(e) => { const next = [...rulesDraft]; next[i] = { ...r, threshold: parseFloat(e.target.value) }; setRulesDraft(next); }} className="cn-input !py-1 !text-[11px] w-20 text-right" />
                        </td>
                        <td className="py-1.5 pr-2 text-right">
                          <input type="number" min={1} max={20} value={r.min_consecutive} onChange={(e) => { const next = [...rulesDraft]; next[i] = { ...r, min_consecutive: parseInt(e.target.value, 10) }; setRulesDraft(next); }} className="cn-input !py-1 !text-[11px] w-12 text-right" />
                        </td>
                        <td className="py-1.5 pr-2 text-right">
                          <input type="number" min={10} max={300} step={10} value={r.window_seconds} onChange={(e) => { const next = [...rulesDraft]; next[i] = { ...r, window_seconds: parseInt(e.target.value, 10) }; setRulesDraft(next); }} className="cn-input !py-1 !text-[11px] w-16 text-right" />s
                        </td>
                        <td className="py-1.5 pr-2">
                          <select value={r.severity || 'warning'} onChange={(e) => { const next = [...rulesDraft]; next[i] = { ...r, severity: e.target.value }; setRulesDraft(next); }} className="cn-select !py-1 !text-[11px]">
                            <option value="warning">warning</option>
                            <option value="critical">critical</option>
                          </select>
                        </td>
                        <td className="py-1.5 text-right">
                          <button onClick={() => setRulesDraft(rulesDraft.filter((_, j) => j !== i))} className="text-cn-text-muted hover:text-red-600 px-2" data-testid={`obs-alerts-rule-remove-${i}`}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={saveRules} disabled={savingRules} className="cn-btn cn-btn-primary !py-1.5 !text-xs flex items-center gap-1.5" data-testid="obs-alerts-save-rules">
                  {savingRules ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Save rules
                </button>
                <button onClick={() => setRulesEditorOpen(false)} className="cn-btn cn-btn-ghost !py-1.5 !text-xs">Cancel</button>
                <p className="text-[11px] text-cn-text-muted ml-auto">Saving resets the consecutive-breach counters so new thresholds take effect cleanly.</p>
              </div>
            </div>
          )}
        </div>

        {/* Long-horizon trend (persistent MongoDB rollups, survives backend restarts) */}
        <div className="cn-card p-5 mb-6" data-testid="obs-history-panel">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2">
                <LineChart className="w-4 h-4 text-cn-text-muted" />
                <h2 className="cn-display text-base text-cn-text">Long-horizon trend</h2>
                {history?.rollup_enabled === false && (
                  <span className="px-2 py-0.5 rounded-md bg-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-wider">
                    Rollups disabled
                  </span>
                )}
                {history?.rollup_enabled && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">
                    <Database className="w-3 h-3" /> Persisted
                  </span>
                )}
              </div>
              <p className="text-[11px] text-cn-text-muted mt-1">
                {history?.samples
                  ? `${history.samples} rollups stored · ${history.rollup_interval_seconds}s cadence · survives restarts`
                  : 'Snapshots taken every 60s — saved to MongoDB so this chart survives a backend restart.'}
              </p>
            </div>
            <select
              value={historyHours}
              onChange={(e) => setHistoryHours(Number(e.target.value))}
              className="cn-select !py-1.5 !text-xs"
              data-testid="obs-history-window-select"
            >
              <option value={1}>Last 1h</option>
              <option value={6}>Last 6h</option>
              <option value={24}>Last 24h</option>
              <option value={168}>Last 7d</option>
            </select>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div data-testid="obs-history-requests">
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Requests / interval</p>
                <p className="text-xs tabular-nums text-cn-text">
                  latest: <span className="font-bold">{history?.points?.length ? history.points[history.points.length - 1].requests : 0}</span>
                </p>
              </div>
              <Sparkline
                points={(history?.points || []).map((p) => p.requests)}
                stroke="#1e40af"
                fill="rgba(30,64,175,0.12)"
                testid="obs-history-requests-spark"
              />
            </div>
            <div data-testid="obs-history-errors">
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Errors / interval (4xx + 5xx + 429)</p>
                <p className="text-xs tabular-nums text-cn-text">
                  latest: <span className="font-bold">{history?.points?.length ? history.points[history.points.length - 1].errors : 0}</span>
                </p>
              </div>
              <Sparkline
                points={(history?.points || []).map((p) => p.errors)}
                stroke="#dc2626"
                fill="rgba(220,38,38,0.12)"
                testid="obs-history-errors-spark"
              />
            </div>
          </div>
        </div>

        {/* Idle (registered, not yet hit) */}
        {registeredOnly.length > 0 && (
          <div className="cn-card p-5" data-testid="obs-idle-domains">
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="w-4 h-4 text-cn-text-muted" />
              <h2 className="cn-display text-base text-cn-text">Idle domains</h2>
              <p className="text-[11px] text-cn-text-muted">
                Registered surfaces with zero traffic in this window
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {registeredOnly.map((d) => (
                <span
                  key={d}
                  className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[11px] font-mono"
                  data-testid={`obs-idle-${d}`}
                >
                  {d}
                </span>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px] text-cn-text-muted mt-6 text-center" data-testid="obs-footnote">
          Live tile reads the middleware&apos;s in-memory ring buffer (5-min retention, resets on restart).
          Long-horizon trend is persisted to MongoDB every 60s — survives restarts. For Grafana / external
          dashboards, scrape <code className="px-1 bg-slate-100 rounded">/api/metrics</code> (Prometheus format).
        </p>
      </div>
    </div>
  );
}
